import { Injectable } from '@angular/core';
import { ContentStoreService, MxLoggerService, SearchResult, ContentGqlService } from '@motivforce/mx-library-angular';
import { IsLoadingService } from '@service-work/is-loading';
import { BehaviorSubject } from 'rxjs';

import { PromotionRestService } from '../api/rest/promotion-rest.service';
import { PromotionType } from '../enum/promotion-type';
import { BigBangItem } from '../model/promotion/big-bang-item';
import { HowItWorksItem } from '../model/promotion/how-it-works-item';
import { SmarterSellerMilestones } from '../model/promotion/MilestoneSmartSeller';
import { PrizesListItem } from '../model/promotion/prizes-list-item';
import { Promotion } from '../model/promotion/promotion';
import { PromotionSearchFilter } from '../model/promotion/promotion-filter';
import { PromotionResult } from '../model/promotion/promotion-result';
import { PromotionResultSearch } from '../model/promotion/promotion-result-search';
import { PromotionSmarterSellerTransaction } from '../model/promotion/promotion-smarter-seller-transaction';
import { PromotionSmarterSellerTransactionSearch } from '../model/promotion/promotion-smarter-seller-transaction-search';
import { UserStoreService } from './user-store.service';

@Injectable({
  providedIn: 'root',
})
export class PromotionStoreService {
  private readonly _currentPromotionsResults = new BehaviorSubject<SearchResult<Promotion> | null>(null);
  readonly currentPromotionsResults$ = this._currentPromotionsResults.asObservable();

  private readonly _promotions = new BehaviorSubject<Promotion[] | null>(null);
  readonly promotions$ = this._promotions.asObservable();

  private readonly _wepLoungePromotions = new BehaviorSubject<Promotion[] | null>(null);
  readonly wepLoungePromotions$ = this._wepLoungePromotions.asObservable();

  private readonly _currentPromotion = new BehaviorSubject<Promotion | null>(null);
  readonly currentPromotion$ = this._currentPromotion.asObservable();

  private readonly _currentQow = new BehaviorSubject<Promotion | null>(null);
  readonly currentQow$ = this._currentQow.asObservable();

  private readonly _currentPow = new BehaviorSubject<Promotion | null>(null);
  readonly currentPow$ = this._currentPow.asObservable();

  private readonly _currentPromotionResultResults = new BehaviorSubject<SearchResult<PromotionResult> | null>(null);
  readonly currentPromotionResultResults$ = this._currentPromotionResultResults.asObservable();

  private readonly _currentSmarterSeller = new BehaviorSubject<Promotion | null>(null);
  readonly currentSmarterSeller$ = this._currentSmarterSeller.asObservable();

  private readonly _smarterSellerMilestones = new BehaviorSubject<SmarterSellerMilestones | null>(null);
  readonly smarterSellerMilestones$ = this._smarterSellerMilestones.asObservable();

  private readonly _howItWorksItem = new BehaviorSubject<HowItWorksItem | null>(null);
  readonly howItWorksItem$ = this._howItWorksItem.asObservable();

  private readonly _prizesListItem = new BehaviorSubject<PrizesListItem | null>(null);
  readonly prizesListItem$ = this._prizesListItem.asObservable();

  private readonly _currentPromotionSmarterSellerTransactionResults =
    new BehaviorSubject<SearchResult<PromotionSmarterSellerTransaction> | null>(null);

  readonly currentPromotionSmarterSellerTransactionResults$ =
    this._currentPromotionSmarterSellerTransactionResults.asObservable();

  constructor(
    private promotionRest: PromotionRestService,
    private isLoadingService: IsLoadingService,
    private mxLogger: MxLoggerService,
    private userStore: UserStoreService,
    private contentStore: ContentStoreService,
    private contentGql: ContentGqlService
  ) {}

  get currentPromotionsResults(): SearchResult<Promotion> | null {
    return this._currentPromotionsResults.getValue();
  }

  get promotions(): Promotion[] | null {
    return this._promotions.getValue();
  }

  get wepLoungePromotions(): Promotion[] | null {
    return this._wepLoungePromotions.getValue();
  }

  get currentPromotion(): Promotion | null {
    return this._currentPromotion.getValue();
  }

  get currentQow(): Promotion | null {
    return this._currentQow.getValue();
  }

  get currentPow(): Promotion | null {
    return this._currentPow.getValue();
  }

  get currentPromotionResultResults(): SearchResult<PromotionResult> | null {
    return this._currentPromotionResultResults.getValue();
  }

  get currentSmarterSeller(): Promotion | null {
    return this._currentSmarterSeller.getValue();
  }

  get smarterSellerMilestones(): SmarterSellerMilestones | null {
    return this._smarterSellerMilestones.getValue();
  }

  get howItWorksItem(): HowItWorksItem | null {
    return this._howItWorksItem.getValue();
  }

  get PrizesListItem(): PrizesListItem | null {
    return this._prizesListItem.getValue();
  }

  get currentPromotionSmarterSellerTransactionResults(): SearchResult<PromotionSmarterSellerTransaction> | null {
    return this._currentPromotionSmarterSellerTransactionResults.getValue();
  }

  searchPromotions(promotionSearch: PromotionSearchFilter, concatResults = false): void {
    this.isLoadingService.add(
      this.promotionRest.searchPromotions(promotionSearch).subscribe((searchResult) => {
        if (concatResults) {
          this._promotions.next([...this.promotions!, ...searchResult.results]);
        } else {
          this._promotions.next([...searchResult.results]);
        }

        this._currentPromotionsResults.next(searchResult);
      })
    );
  }

  getPromotions(): void {
    this.isLoadingService.add(
      this.promotionRest.getPromotions().subscribe((promotions) => {
        this._promotions.next(promotions);
      })
    );
  }

  getPromotion(promotionId: number): void {
    this.isLoadingService.add(
      this.promotionRest.getPromotion(promotionId).subscribe((promotion) => {
        this._currentPromotion.next(promotion);
      })
    );
  }

  getAvailableUserQow(): void {
    this.isLoadingService.add(
      this.promotionRest.getAvailableUserPromotions(PromotionType.QOW).subscribe((promotions: Array<any>) => {
        if (promotions && promotions.length > 0) {
          this._currentQow.next(promotions[0]);
        } else {
          this._currentQow.next(null);
        }
      })
    );
  }

  getAvailableUserPow(): void {
    this.isLoadingService.add(
      this.promotionRest.getAvailableUserPromotions(PromotionType.POW).subscribe((promotions: Array<any>) => {
        if (promotions && promotions.length > 0) {
          this._currentPow.next(promotions[0]);
        } else {
          this._currentPow.next(null);
        }
      })
    );
  }

  getActiveQow(): void {
    this.isLoadingService.add(
      this.promotionRest.getActivePromotions(PromotionType.QOW).subscribe((promotions: Array<any>) => {
        if (promotions && promotions.length > 0) {
          this._currentPromotion.next(promotions[0]);
        }
      })
    );
  }

  getActivePow(): void {
    this.isLoadingService.add(
      this.promotionRest.getActivePromotions(PromotionType.POW).subscribe((promotions: Array<any>) => {
        if (promotions && promotions.length > 0) {
          this._currentPromotion.next(promotions[0]);
        }
      })
    );
  }

  getSmartSellerPromotion(): void {
    this.isLoadingService.add(
      this.promotionRest.getAvailableUserPromotions(PromotionType.SMART_SELLER).subscribe((promotions: Array<any>) => {
        if (promotions && promotions.length > 0) {
          this._currentPromotion.next(promotions[0]);
        } else {
          this._currentPromotion.next(null);
        }
      })
    );
  }

  getAvailableUserSmartSellerPromotion(): void {
    this.isLoadingService.add(
      this.promotionRest.getAvailableUserPromotions(PromotionType.SMART_SELLER).subscribe((promotions: Array<any>) => {
        if (promotions && promotions.length > 0) {
          this._currentSmarterSeller.next(promotions[0]);
        } else {
          this._currentSmarterSeller.next(null);
        }
      })
    );
  }

  getSmarterSellerMilestones(promotionId: number): void {
    this.isLoadingService.add(
      this.promotionRest
        .getSmarterSellerMilestones(promotionId)
        .subscribe((smarterSellerMilestones: SmarterSellerMilestones) => {
          this._smarterSellerMilestones.next(smarterSellerMilestones);
        })
    );
  }

  searchResults(resultSearch: PromotionResultSearch): void {
    this.isLoadingService.add(
      this.promotionRest.searchResults(resultSearch).subscribe((searchResults) => {
        this._currentPromotionResultResults.next(searchResults);
      })
    );
  }

  getWEPLoungeProducts(): void {
    this.isLoadingService.add(
      this.promotionRest.getWEPLoungeProducts().subscribe((products) => {
        this._wepLoungePromotions.next(products);
      })
    );
  }

  referFriend(promotionId: number, email: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.isLoadingService.add(
        this.promotionRest.referFriend(promotionId, email).subscribe({
          next: () => {
            resolve(true);
          },
          error: () => resolve(false),
        })
      );
    });
  }

  awardEventReferral(urlKey: string, promotionId: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.isLoadingService.add(
        this.promotionRest.awardEventReferral(urlKey, promotionId).subscribe({
          next: () => {
            resolve(true);
          },
          error: () => resolve(false),
        })
      );
    });
  }

  submitTnC(promotionId: number, jobTitle?: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.isLoadingService.add(
        this.promotionRest.submitTnC(promotionId, jobTitle).subscribe({
          next: () => {
            resolve(true);
          },
          error: () => resolve(false),
        })
      );
    });
  }

  searchPromotionSmarterSellerTransactions(
    promotionId: number,
    searchCriteria: PromotionSmarterSellerTransactionSearch
  ): void {
    this.isLoadingService.add(
      this.promotionRest
        .searchPromotionSmarterSellerTransactions(promotionId, searchCriteria)
        .subscribe((searchResult: SearchResult<PromotionSmarterSellerTransaction>) => {
          this._currentPromotionSmarterSellerTransactionResults.next(searchResult);
        })
    );
  }

  getHowItWorksContent(url: string | undefined) {
    const siteUrl = new URL(window.location.href);
    const path = `${siteUrl.origin}${url}`;
    this.contentGql.getContentByUrlPath(path, this.contentStore.currentLanguage, true).subscribe((content) => {
      const first = content.children.find((f: any) => f.name === '1.Sell');
      const second = content.children.find((f: any) => f.name === '2.Earn');
      const third = content.children.find((f: any) => f.name === '3.WinBig');
      const howItWorksItem: HowItWorksItem = {
        firstItem: {
          text: first.fields.find((f: any) => f.name === 'Text').value.value,
          banner: first.fields.find((f: any) => f.name === 'Banner').value.url,
        },
        secondItem: {
          text: second.fields.find((f: any) => f.name === 'Text').value.value,
          banner: second.fields.find((f: any) => f.name === 'Banner').value.url,
        },
        thirdItem: {
          text: third.fields.find((f: any) => f.name === 'Text').value.value,
          banner: third.fields.find((f: any) => f.name === 'Banner').value.url,
        },
      };

      this._howItWorksItem.next(howItWorksItem);
    });
  }

  getPrizesListContent(url: string | undefined) {
    const siteUrl = new URL(window.location.href);
    const path = `${siteUrl.origin}${url}`;
    this.contentGql.getContentByUrlPath(path, this.contentStore.currentLanguage, true).subscribe((content) => {
      const prizes: BigBangItem[] = [];
      let grandPrize: BigBangItem | null = null;

      content.children.forEach((item: any) => {
        const prize: BigBangItem = {
          text: item.fields.find((f: any) => f.name === 'Text').value.value,
          banner: item.fields.find((f: any) => f.name === 'Banner').value.url,
        };

        if (prize.text?.toLowerCase().includes('grand prize')) {
          grandPrize = prize;
        } else {
          prizes.push(prize);
        }
      });

      this._prizesListItem.next({
        grandPrize,
        prizes,
      });
    });
  }
}
